import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./CoachingCheckout.css";
import Navbar from "../components/Navbar";
import { supabase } from "../supabase";
import { SST_RATE, SST_LABEL, coachingGroupLabel } from "../constants";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { parsePhoneNumber } from "libphonenumber-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

const ConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Confirm Order</h2>
        <div className="modal-body">
          <p>By clicking Agree, you confirm all items in your order are correct.</p>
          <p>
            <strong>Refund Policy (Last updated: 05/06/2025):</strong><br />
            - Cancel anytime as long as it is not within 48 hours before the booking time for a full refund as credits.<br />
            - No refunds within 48 hours of the booking time.<br />
            - Credits are non-transferable, non-redeemable for cash.<br />
            - No cash refunds.<br />
            - For issues, contact <a href="mailto:info@pickledepot.com.my">info@pickledepot.com.my</a>.<br />
            Full Terms: <a href="https://www.pickledepot.com.my/_files/ugd/01db86_19a89a1d4ff843b39265478a620528a0.pdf" target="_blank" rel="noopener noreferrer">here</a>.
          </p>
        </div>
        <div className="modal-buttons">
          <button className="modal-close-button" onClick={onClose} aria-label="Cancel order confirmation">Cancel</button>
          <button className="modal-confirm-button" onClick={onConfirm} aria-label="Agree to terms and confirm order"> Agree</button>
        </div>
      </div>
    </div>
  );
};

const CoachingCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    selectedSport,
    selectedCoach,
    selectedDate,
    selectedTimeslots,
    pax,
    bookingDate,
    selectedBookingType,
    selectedProfile,
    classType,
    perSlotPrice,
  } = location.state || {};

  const isGroup = classType === 'group';
  const groupBlockSize = Math.max(1, selectedCoach?.groupBlockSize || 1);

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [useCredits, setUseCredits] = useState(true);
  const [creditsToUse, setCreditsToUse] = useState(0);
  const [discountedTotal, setDiscountedTotal] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [notification, setNotification] = useState({ message: "", show: false });
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeApplied, setPromoCodeApplied] = useState(null);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [isPromoLoading, setIsPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    billingName: "",
    billingEmail: "",
    billingMobile: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const paymentFormRef = useRef(null);
  const phoneInputRef = useRef(null);
  const phoneInputContainerRef = useRef(null);

  const fixedBillingAddress = "WS4, Sentul Depot, Jalan Strachan, Sentul West, 51100 Kuala Lumpur, Wilayah Persekutuan Kuala Lumpur";
  const memoizedLocationState = useMemo(() => location.state, [location.state]);

  // Pricing derives from perSlotPrice (the coaching_booking_price RPC value, passed in
  // from PrivateLessonForm) — the single source of truth. sessionPrice is per session
  // (group = perSlotPrice × block_size = group_price; private = perSlotPrice).
  const perSlot = Number(perSlotPrice) || 0;
  const sessionPrice = isGroup ? perSlot * groupBlockSize : perSlot;
  const timeslotCount = selectedTimeslots?.length || 1;
  const sessionCount = isGroup ? Math.floor(timeslotCount / groupBlockSize) : timeslotCount;

  const mapBookingType = (type) => {
    switch (type) {
      case "court-booking": return "court_booking";
      case "group-lesson": return "group_lesson";
      case "private-lesson": return "private_lesson";
      default: return null;
    }
  };

  const isSafari = () => {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes("safari") && !ua.includes("chrome");
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const debouncedValidate = useRef(
    debounce((name, value) => {
      let normalizedValue = value;

      if (name === "billingMobile" && value) {
        try {
          const phoneNumber = parsePhoneNumber(value);
          if (phoneNumber?.isValid()) {
            normalizedValue = phoneNumber.formatInternational();
          }
        } catch {}
      }

      setFormErrors((prev) => {
        const errors = { ...prev };
        if (!normalizedValue || normalizedValue.trim() === "") {
          errors[name] = `${name.replace("billing", "Billing ")} is required`;
        } else {
          delete errors[name];
        }
        if (name === "billingName" && normalizedValue) {
          if (!/^[a-zA-Z0-9\s]+$/.test(normalizedValue)) {
            errors.billingName = "Billing Name must be alphanumeric";
          } else if (normalizedValue.length > 128) {
            errors.billingName = "Billing Name must be up to 128 characters";
          }
        }
        if (name === "billingEmail" && normalizedValue) {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedValue)) {
            errors.billingEmail = "Invalid email format";
          } else if (normalizedValue.length > 128) {
            errors.billingEmail = "Billing Email must be up to 128 characters";
          }
        }
        if (name === "billingMobile" && normalizedValue) {
          const phoneError = validatePhoneNumber(normalizedValue);
          if (phoneError) {
            errors.billingMobile = phoneError;
          } else {
            delete errors.billingMobile;
          }
        }
        return errors;
      });
    }, 300)
  ).current;

  // Detect autofilled values on mount
  useEffect(() => {
    const checkInitialValues = () => {
      const billingNameInput = document.getElementById("billingName");
      if (billingNameInput && billingNameInput.value && !formData.billingName) {
        handleChange("billingName", billingNameInput.value);
        debouncedValidate("billingName", billingNameInput.value);
      }
      const billingEmailInput = document.getElementById("billingEmail");
      if (billingEmailInput && billingEmailInput.value && !formData.billingEmail) {
        handleChange("billingEmail", billingEmailInput.value);
        debouncedValidate("billingEmail", billingEmailInput.value);
      }
      if (phoneInputRef.current && phoneInputRef.current.value && !formData.billingMobile) {
        handleChange("billingMobile", phoneInputRef.current.value);
        debouncedValidate("billingMobile", phoneInputRef.current.value);
      }
    };
    setTimeout(checkInitialValues, 100);
  }, []);

  // Auth check and profile/credits fetch
  useEffect(() => {
    const checkSession = async () => {
      let hasRedirected = false;
      const redirectToLogin = () => {
        if (!hasRedirected) {
          hasRedirected = true;
          navigate("/login", { state: { from: location.pathname, ...memoizedLocationState } });
        }
      };
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error || !session) {
          redirectToLogin();
          return;
        }
        setIsAuthenticated(true);
        const { data: user, error: userError } = await supabase.auth.getUser();
        if (userError || !user) {
          console.error("CoachingCheckout.js: Error fetching user:", userError);
          // getUser() hits the network; when it fails because the connection
          // dropped (status 0 / "Load failed") the local session is still
          // valid and redirecting ping-pongs with Login's session check.
          // Only kick to /login when the server actually rejected the token.
          if (!userError || userError.status === 401 || userError.status === 403) {
            redirectToLogin();
          }
          return;
        }
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("membership_status")
          .eq("user_id", user.user.id)
          .single();
        if (profileError && profileError.code !== "PGRST116") {
          console.error("CoachingCheckout.js: Error fetching profile:", profileError);
          redirectToLogin();
          return;
        }
        setMembershipStatus(profileData?.membership_status || "normal_member");

        const { data: creditsData, error: creditsError } = await supabase
          .from("credits")
          .select("balance")
          .eq("user_id", user.user.id)
          .single();
        if (creditsError) {
          if (creditsError.code === "PGRST116") setAvailableCredits(0);
          else {
            console.error("CoachingCheckout.js: Error fetching credits:", creditsError);
            redirectToLogin();
            return;
          }
        } else {
          setAvailableCredits(creditsData?.balance || 0);
        }

        // Autofill from latest order
        const { data: orderData, error: orderError } = await supabase
          .from("orders")
          .select("billing_name, billing_email, billing_mobile")
          .eq("user_id", user.user.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .single();
        if (orderError && orderError.code !== "PGRST116") {
          console.error("CoachingCheckout.js: Error fetching latest order:", orderError);
        } else if (orderData) {
          setFormData({
            billingName: orderData.billing_name || "",
            billingEmail: orderData.billing_email || "",
            billingMobile: orderData.billing_mobile || "",
          });
        }
      } catch (error) {
        console.error("CoachingCheckout.js: Unexpected error in checkSession:", error);
        redirectToLogin();
      } finally {
        setIsLoading(false);
      }
    };
    checkSession();
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        navigate("/login", { state: { from: location.pathname, ...memoizedLocationState } });
      }
    });
    return () => authListener.subscription.unsubscribe();
  }, [navigate, location.pathname, memoizedLocationState]);

  // Poll for autofill in Safari and other browsers
  useEffect(() => {
    if (!phoneInputRef.current) return;
    const checkForAutofill = () => {
      const input = phoneInputRef.current;
      if (!input) return;
      const currentValue = input.value;
      const stateValue = formData.billingMobile;
      if (currentValue && currentValue !== stateValue) {
        handleChange("billingMobile", currentValue || "");
        debouncedValidate("billingMobile", currentValue || "");
      }
      const billingNameInput = document.getElementById("billingName");
      if (billingNameInput && billingNameInput.value && billingNameInput.value !== formData.billingName) {
        handleChange("billingName", billingNameInput.value);
        debouncedValidate("billingName", billingNameInput.value);
      }
      const billingEmailInput = document.getElementById("billingEmail");
      if (billingEmailInput && billingEmailInput.value && billingEmailInput.value !== formData.billingEmail) {
        handleChange("billingEmail", billingEmailInput.value);
        debouncedValidate("billingEmail", billingEmailInput.value);
      }
    };
    const intervalTime = isSafari() ? 100 : 500;
    const interval = setInterval(checkForAutofill, intervalTime);
    const timeout = setTimeout(() => clearInterval(interval), 5000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [formData.billingMobile, formData.billingName, formData.billingEmail]);

  // Recalculate credits to use when toggle or available credits change
  useEffect(() => {
    if (useCredits) {
      const maxCredits = Math.min(availableCredits, Math.max(0, discountedTotal - promoDiscount));
      setCreditsToUse(maxCredits);
    } else {
      setCreditsToUse(0);
    }
  }, [availableCredits, discountedTotal, useCredits, promoDiscount]);

  // Recalculate totals
  useEffect(() => {
    const total = sessionPrice * sessionCount;
    setDiscountedTotal(total);
    const finalAmount = Math.max(0, total - promoDiscount - (useCredits ? creditsToUse : 0));
    setFinalTotal(finalAmount);
  }, [sessionPrice, sessionCount, useCredits, creditsToUse, promoDiscount]);

  const handlePhoneBlur = () => {
    if (phoneInputRef.current) {
      const inputValue = phoneInputRef.current.value;
      if (inputValue && inputValue !== formData.billingMobile) {
        handleChange("billingMobile", inputValue || "");
        debouncedValidate("billingMobile", inputValue || "");
      }
    }
  };

  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date)) return "Invalid Date";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const parseTimeslot = (timeslot) => {
    if (!timeslot) return "";
    const [time, period] = timeslot.split(" ");
    let [hours, minutes] = time.split(":").map(Number);
    let endHours = hours + 1;
    let endPeriod = period;
    if (hours === 12 && period === "PM") { endHours = 1; endPeriod = "PM"; }
    else if (hours === 12 && period === "AM") { endHours = 1; endPeriod = "AM"; }
    else if (endHours === 12) endPeriod = period === "AM" ? "PM" : "AM";
    else if (endHours > 12) { endHours -= 12; endPeriod = period === "AM" ? "PM" : "AM"; }
    const endTime = `${endHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${endPeriod}`;
    return `${timeslot} - ${endTime}`;
  };

  const validatePhoneNumber = (value) => {
    if (!value) return "Billing Mobile is required";
    try {
      const phoneNumber = parsePhoneNumber(value);
      if (!phoneNumber || !phoneNumber.isValid()) return "Invalid phone number (e.g., +12025550123)";
      return null;
    } catch (error) {
      console.error("CoachingCheckout.js: Error validating phone number:", error);
      return "Invalid phone number format";
    }
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((name) => {
      if (!formData[name] || formData[name].trim() === "") {
        errors[name] = `${name.replace("billing", "Billing ")} is required`;
      }
      if (name === "billingName" && formData[name]) {
        if (!/^[a-zA-Z0-9\s]+$/.test(formData[name])) {
          errors.billingName = "Billing Name must be alphanumeric";
        } else if (formData[name].length > 128) {
          errors.billingName = "Billing Name must be up to 128 characters";
        }
      }
      if (name === "billingEmail" && formData[name]) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[name])) {
          errors.billingEmail = "Invalid email format";
        } else if (formData[name].length > 128) {
          errors.billingEmail = "Billing Email must be up to 128 characters";
        }
      }
      if (name === "billingMobile" && formData[name]) {
        const phoneError = validatePhoneNumber(formData[name]);
        if (phoneError) errors.billingMobile = phoneError;
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateOrderId = async () => {
    const uuid = crypto.randomUUID().replace(/-/g, "").substring(0, 8);
    const timestamp = Date.now();
    return `order_${uuid}_${timestamp}`.substring(0, 40);
  };

  const cleanupExpiredCreditsForUser = async (userId) => {
    try {
      const now = new Date().toISOString();
      const { data: expiredReservations, error: fetchError } = await supabase
        .from("pending_credits_deduction")
        .select("order_id")
        .eq("user_id", userId)
        .lt("expires_at", now);
      if (fetchError) {
        console.error("CoachingCheckout.js: Error fetching expired credit reservations for user:", fetchError);
        return false;
      }
      if (expiredReservations && expiredReservations.length > 0) {
        const orderIds = expiredReservations.map((reservation) => reservation.order_id);
        const { data: cleanUpResponse, error: cleanUpError } = await supabase.functions.invoke(
          "client-delete-pending-credits-deduction",
          { body: { order_ids: orderIds } }
        );
        console.log(`CoachingCheckout.js: Cleaned up ${expiredReservations.length} expired credit reservations for user ${userId}`);
      }
      return true;
    } catch (error) {
      console.error("CoachingCheckout.js: Unexpected error cleaning up expired credits:", error);
      return false;
    }
  };

  const reserveCredits = async (userId, orderId, credits) => {
    try {
      const insertData = { order_id: orderId, credits };
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + 3);
      insertData.expires_at = expiresAt.toISOString();
      const { data: reserveResponse, error: reserveError } = await supabase.functions.invoke(
        "client-insert-pending-credits-deduction",
        { body: { insert_data: insertData } }
      );
      if (reserveError) {
        console.error("CoachingCheckout.js: Error reserving credits:", reserveError);
        return false;
      }
      return true;
    } catch (error) {
      console.error("CoachingCheckout.js: Unexpected error reserving credits:", error);
      return false;
    }
  };

  const generateOrderItems = useCallback(() => {
    const items = [];
    const dateObj = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
    const itemType = isGroup
      ? coachingGroupLabel(selectedCoach?.groupLabel, selectedSport)
      : "Private Lesson";
    // Chunk timeslots into sessions (block_size timeslots per session for Group, 1 for Private)
    const slots = selectedTimeslots || [];
    for (let i = 0; i < slots.length; i += groupBlockSize) {
      const chunk = slots.slice(i, i + groupBlockSize);
      const timeslotLabel = chunk.length === 1
        ? parseTimeslot(chunk[0])
        : `${chunk[0]} – ${parseTimeslot(chunk[chunk.length - 1]).split(' - ')[1]}`;
      items.push({
        id: `coaching_session_${(i / groupBlockSize) + 1}`,
        type: itemType,
        details: `${selectedSport} Coaching with ${selectedCoach?.name || "Coach"}\n${formatDate(dateObj)}, ${timeslotLabel}\n${pax || 1} pax`,
        detailsForBackend: `${selectedSport} Coaching with ${selectedCoach?.name || "Coach"}\n${formatDate(dateObj)}, ${timeslotLabel}\n${pax || 1} pax`,
        price: sessionPrice,
        basePrice: sessionPrice,
        isFreeSlot: false,
      });
    }
    if (useCredits && creditsToUse > 0) {
      items.push({
        id: "credits",
        type: "Credits Applied",
        details: "Credits Used",
        detailsForBackend: "Credits Used",
        price: -creditsToUse,
        isFreeSlot: false,
      });
    }
    return items;
  }, [selectedSport, selectedCoach, selectedDate, selectedTimeslots, pax, sessionPrice, useCredits, creditsToUse, isGroup, groupBlockSize]);

  const orderItems = useMemo(() => generateOrderItems(), [generateOrderItems]);

  const handleCreditsToggle = () => {
    setUseCredits(!useCredits);
    if (!useCredits) {
      const maxCredits = Math.min(availableCredits, Math.max(0, discountedTotal - promoDiscount));
      setCreditsToUse(maxCredits);
    } else {
      setCreditsToUse(0);
    }
  };

  // Reset promo when booking selections change
  useEffect(() => {
    setPromoCode('');
    setPromoCodeApplied(null);
    setPromoDiscount(0);
    setPromoError('');
  }, [selectedCoach, selectedDate, selectedTimeslots]);

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return;
    setIsPromoLoading(true);
    setPromoError('');

    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const orderAmount = discountedTotal;

      // Booking context for promo constraints. Coaching = private_lesson.
      const localDate = selectedDate ? new Date(selectedDate.getTime() + (8 * 60 * 60 * 1000)) : null;
      const bookingDate = localDate
        ? `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, "0")}-${String(localDate.getDate()).padStart(2, "0")}`
        : null;

      const response = await fetch(
        'https://api.pickledepot.com.my/functions/v1/validate-promo-code',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im12dmhycGZhY2dtbmZpZ254dGtiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUwNjIzNjAsImV4cCI6MjA2MDYzODM2MH0.mHAiPSOC3pS5vTBcQB-__TsziwT30gnyI9aVuxeSazw'
          },
          body: JSON.stringify({
            code: promoCode.trim(),
            order_amount: orderAmount,
            booking_type: 'private_lesson',
            sport_name: selectedSport,
            timeslots: Array.isArray(selectedTimeslots) ? selectedTimeslots : [],
            booking_date: bookingDate,
          })
        }
      );

      const data = await response.json();

      if (data.valid) {
        setPromoCodeApplied(data);
        setPromoDiscount(data.discount_amount);
        setPromoError('');
      } else {
        setPromoError(data.error || 'Invalid promo code');
        setPromoCodeApplied(null);
        setPromoDiscount(0);
      }
    } catch (err) {
      setPromoError('Failed to validate promo code. Please try again.');
    } finally {
      setIsPromoLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
    setPromoCode('');
    setPromoCodeApplied(null);
    setPromoDiscount(0);
    setPromoError('');
  };

  const handleCloseNotification = () => {
    setNotification({ message: "", show: false });
  };

  const handlePlaceOrder = (event) => {
    event.preventDefault();
    setShowConfirmationModal(true);
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();

    setShowConfirmationModal(false);
    setIsProcessing(true);

    if (phoneInputRef.current) {
      phoneInputRef.current.blur();
    }

    if (!validateForm()) {
      setNotification({ message: "Please fill in all required fields correctly.", show: true });
      setIsProcessing(false);
      return;
    }

    // Refresh session before payment flow
    const { data: { session: orderSession }, error: orderSessionError } = await supabase.auth.getSession();
    if (orderSessionError || !orderSession) {
      setNotification({ message: "Your session has expired. Please log in again.", show: true });
      setIsProcessing(false);
      navigate("/login", { state: { from: location.pathname, ...memoizedLocationState } });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setIsProcessing(false);
      navigate("/login", { state: { from: location.pathname, ...memoizedLocationState } });
      return;
    }

    const orderId = await generateOrderId();
    const sanitizeDescription = (desc) =>
      desc.replace(/[^a-zA-Z0-9\s;-]/g, "").substring(0, 128);
    const billDesc = sanitizeDescription(
      orderItems
        .map((item) => `${item.type} - ${item.detailsForBackend.replace(/\n/g, "; ")}`)
        .join("; ")
    );

    const bookingType = mapBookingType(selectedBookingType);
    if (!bookingType) {
      setNotification({
        message: "Invalid booking selection. Please go back and try again.",
        show: true,
      });
      setIsProcessing(false);
      return;
    }

    const bookingMetadata = {
      type: "private_lesson",
      coach_id: selectedCoach?.id,
      coach_name: selectedCoach?.name,
      sport_name: selectedSport,
      booking_date: bookingDate,
      timeslots: selectedTimeslots,
      pax: pax || 1,
      credits_to_use: creditsToUse,
      class_type: classType || null,
    };

    // Only include fields that exist in the orders table
    const orderRequestBody = {
      order_id: orderId,
      total_amount: finalTotal,
      status: finalTotal > 0 ? "pending" : "completed",
      billing_name: formData.billingName,
      billing_email: formData.billingEmail,
      billing_mobile: formData.billingMobile.replace(/\s/g, ""),
      billing_address: fixedBillingAddress,
      booking_metadata: bookingMetadata,
    };

    if (finalTotal === 0) {
      // Fully paid with credits — call free order endpoint
      const { data: createFreeOrderResponse, error: createFreeOrderError } =
        await supabase.functions.invoke("client-create-order-free-v8", {
          body: { order_request_body: orderRequestBody, sub_orders: [{ order_id: orderId, booking_metadata: bookingMetadata }], booking_type: "private_lesson", use_credits: useCredits, credits_to_use: creditsToUse },
        });
      if (createFreeOrderError) {
        console.error("CoachingCheckout.js: Failed to confirm free coaching booking:", createFreeOrderError);
        let errorMessage = "Failed to confirm booking. Please try again.";
        try {
          if (createFreeOrderError.context?.json) {
            const body = await createFreeOrderError.context.json();
            errorMessage = body?.error || errorMessage;
          }
        } catch {}
        setNotification({ message: errorMessage, show: true });
        setIsProcessing(false);
        return;
      }
      // Check for success:false in response body (2xx but logical failure)
      if (createFreeOrderResponse && createFreeOrderResponse.success === false) {
        setNotification({
          message: createFreeOrderResponse.error || "Failed to confirm booking. Please try again.",
          show: true,
        });
        setIsProcessing(false);
        return;
      }
      setNotification({ message: "Booking confirmed successfully!", show: true });
      setTimeout(() => navigate("/profile", { state: { selectedProfile: true } }), 2000);
      return;
    }

    // Paid booking flow
    if (useCredits && creditsToUse > 0) {
      const cleanupSuccess = await cleanupExpiredCreditsForUser(user.id);
      if (!cleanupSuccess) {
        setNotification({
          message: "Failed to clean up expired credit reservations. Please try again or contact support.",
          show: true,
        });
        setIsProcessing(false);
        return;
      }
    }

    const subOrderId = await generateOrderId();
    const { data: paymentParams, error: createOrderError } = await supabase.functions.invoke(
      "client-create-order-v12",
      { body: {
        main_order: orderRequestBody,
        sub_orders: [{
          order_id: subOrderId,
          parent_order_id: orderId,
          total_amount: finalTotal,
          billing_name: formData.billingName,
          billing_email: formData.billingEmail,
          billing_mobile: formData.billingMobile.replace(/\s/g, ""),
          billing_address: fixedBillingAddress,
          status: "pending",
          booking_metadata: bookingMetadata,
        }],
        ...(promoCodeApplied ? { promo_code: promoCodeApplied.code } : {}),
        ...(promoDiscount > 0 ? { promo_discount_amount: promoDiscount } : {}),
        // Pre-promo, pre-credits subtotal — the same basis validate-promo-code
        // scored at Apply time, so the backend re-check agrees with the preview.
        pre_discount_total: discountedTotal,
      } }
    );

    if (createOrderError) {
      let errorMessage = "Failed to make booking. Please try again or contact support.";
      if (createOrderError.context && createOrderError.context.status) {
        const status = createOrderError.context.status;
        const backendBody = await createOrderError.context.json();
        if (status === 400) {
          errorMessage = backendBody?.details || "Invalid request details provided.";
        } else if (status === 409) {
          errorMessage = backendBody?.details || "The selected timeslot is no longer available. Please choose a different one.";
        } else if (status === 410) {
          errorMessage = backendBody?.details || "Your pending booking expired or became invalid. Please start a new booking.";
        } else if (status === 500) {
          errorMessage = backendBody?.details || "Internal server error during order creation.";
        }
      } else {
        errorMessage = createOrderError.message || errorMessage;
      }
      setNotification({ message: errorMessage, show: true });
      if (useCredits && creditsToUse > 0) {
        await supabase.functions.invoke("client-delete-pending-credits-deduction", {
          body: { order_ids: [orderId] },
        });
      }
      setIsProcessing(false);
      return;
    }

    if (paymentFormRef.current) {
      const form = paymentFormRef.current;
      form.querySelector('input[name="merchant_id"]').value = paymentParams.merchant_id;
      form.querySelector('input[name="amount"]').value = paymentParams.amount;
      form.querySelector('input[name="orderid"]').value = paymentParams.order_id;
      form.querySelector('input[name="currency"]').value = paymentParams.currency;
      form.querySelector('input[name="vcode"]').value = paymentParams.vcode;
      form.querySelector('input[name="bill_name"]').value = formData.billingName;
      form.querySelector('input[name="bill_email"]').value = formData.billingEmail;
      form.querySelector('input[name="bill_mobile"]').value = formData.billingMobile.replace(/\s/g, "");
      form.querySelector('input[name="bill_desc"]').value = billDesc;
      form.querySelector('input[name="b_addr1"]').value = fixedBillingAddress;
      form.querySelector('input[name="returnurl"]').value = paymentParams.return_url;
      form.querySelector('input[name="notifyurl"]').value = paymentParams.notify_url;
      form.querySelector('input[name="callbackurl"]').value = paymentParams.callback_url;
      form.querySelector('input[name="metadata"]').value = JSON.stringify({ booking_type: bookingType });
      form.querySelector('input[name="waittime"]').value = paymentParams.waittime;
      form.querySelector('input[name="idempotency_key"]').value = paymentParams.idempotency_key;
      form.querySelector('input[name="guest_checkout"]').value = "0";

      const params = new FormData(form);
      console.log("CoachingCheckout.js: Payment parameters being sent:", Object.fromEntries(params));

      form.submit();
    }
  };

  if (isLoading) return <div className="loading-spinner-container"><div className="loading-spinner"></div></div>;
  if (!isAuthenticated) return null;

  return (
    <div className="checkout-container">
      <Navbar
        selectedSport={selectedSport}
        selectedBookingType={null}
        onBookingTypeSelect={(type) => navigate("/", { state: { selectedBookingType: type } })}
        onProfileClick={() => navigate("/profile", { state: { selectedProfile: true } })}
        isProfileActive={selectedProfile || false}
      />
      <div className="checkout-form">
        <div className="checkout-content">
          <div className="checkout-left">
            <div className="checkout-section">
              <h2 className="checkout-section-header">Billing Information</h2>
              <div className="form-group">
                <label htmlFor="billingName">Full Name <span className="required-marker">*</span></label>
                <input
                  type="text"
                  id="billingName"
                  name="billingName"
                  value={formData.billingName}
                  onChange={(e) => {
                    handleChange(e.target.name, e.target.value);
                    debouncedValidate(e.target.name, e.target.value);
                  }}
                  className="checkout-input"
                  placeholder="Enter your full name"
                  autoComplete="name"
                />
                {formErrors.billingName && <span className="error">{formErrors.billingName}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="billingEmail">Email Address <span className="required-marker">*</span></label>
                <input
                  type="email"
                  id="billingEmail"
                  name="billingEmail"
                  value={formData.billingEmail}
                  onChange={(e) => {
                    handleChange(e.target.name, e.target.value);
                    debouncedValidate(e.target.name, e.target.value);
                  }}
                  className="checkout-input"
                  placeholder="Enter your email"
                  autoComplete="email"
                />
                {formErrors.billingEmail && <span className="error">{formErrors.billingEmail}</span>}
              </div>
              <div className="form-group" ref={phoneInputContainerRef}>
                <label htmlFor="bookingMobile">Mobile Number <span className="required-marker">*</span></label>
                <input
                  type="tel"
                  name="billingMobile"
                  id="billingMobile-native"
                  autoComplete="tel"
                  style={{ position: "absolute", opacity: 0, height: 0, pointerEvents: "none" }}
                  onChange={(e) => {
                    handleChange("billingMobile", e.target.value);
                    debouncedValidate("billingMobile", e.target.value);
                  }}
                  value={formData.billingMobile}
                />
                <PhoneInput
                  international
                  value={formData.billingMobile}
                  onChange={(value) => {
                    handleChange("billingMobile", value);
                    debouncedValidate("billingMobile", value);
                  }}
                  onBlur={handlePhoneBlur}
                  placeholder="Enter your phone number (e.g., +12025550123)"
                  inputRef={phoneInputRef}
                  inputProps={{ name: "billingMobile", autoComplete: "tel", type: "tel", id: "billingMobile" }}
                />
                {formErrors.billingMobile && <span className="error">{formErrors.billingMobile}</span>}
              </div>
            </div>
          </div>
          <div className="checkout-right">
            <div className="checkout-section">
              <h2 className="checkout-section-header">Order Summary</h2>
              <div className="order-summary">
                <div className="order-items-scrollable">
                  {orderItems.length > 0 ? (
                    orderItems.map((item, index) => (
                      <div className="summary-item" key={index}>
                        <span style={{ whiteSpace: "pre-line" }}>
                          {item.type} - {item.details}
                        </span>
                        <span style={{ whiteSpace: "pre-line" }}>
                          {item.type === "Credits Applied"
                            ? `- RM ${Math.abs(item.price).toFixed(2)}`
                            : item.price === 0
                            ? ""
                            : `RM ${item.basePrice.toFixed(2)}`}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="summary-item"><span>No items in the order</span></div>
                  )}
                  {promoDiscount > 0 && promoCodeApplied && (
                    <div className="summary-item">
                      <span>Promo Code Discount ({promoCodeApplied.code})</span>
                      <span>- RM {promoDiscount.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div className="order-totals-static">
                  {/* Promo Code Section */}
                  <div className="promo-code-section">
                    {!promoCodeApplied ? (
                      <div className="promo-code-input-row">
                        <input
                          type="text"
                          className="promo-code-input"
                          placeholder="Enter promo code"
                          value={promoCode}
                          onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                          onKeyDown={(e) => e.key === 'Enter' && handleApplyPromoCode()}
                          disabled={isPromoLoading}
                        />
                        <button
                          className="promo-apply-btn"
                          onClick={handleApplyPromoCode}
                          disabled={isPromoLoading || !promoCode.trim()}
                        >
                          {isPromoLoading ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                    ) : (
                      <div className="promo-applied-row">
                        <span className="promo-applied-label">
                          Promo: <strong>{promoCodeApplied.code}</strong>
                          {promoCodeApplied.description && <span className="promo-description"> — {promoCodeApplied.description}</span>}
                        </span>
                        <span className="promo-applied-discount">-RM {promoDiscount.toFixed(2)}</span>
                        <button className="promo-remove-btn" onClick={handleRemovePromoCode}>&#x2715;</button>
                      </div>
                    )}
                    {promoError && <p className="promo-error">{promoError}</p>}
                  </div>
                  {availableCredits > 0 && (
                    <div className="credits-section">
                      <label className="credits-label">
                        <input type="checkbox" checked={useCredits} onChange={handleCreditsToggle} />
                        Use Credits (Available: RM {availableCredits.toFixed(2)})
                      </label>
                      {useCredits && (
                        <div className="credits-input-container">
                          <label>Amount to Use:</label>
                          <span
                            className="credits-input"
                            style={{ border: "none", background: "transparent", padding: "8px" }}
                          >
                            RM {creditsToUse.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="summary-subtotal">
                    <span>Subtotal</span>
                    <span>RM {finalTotal.toFixed(2)}</span>
                  </div>
                  <div className="summary-subtotal">
                    <span>{SST_LABEL}</span>
                    <span>RM {(finalTotal * SST_RATE).toFixed(2)}</span>
                  </div>
                  <div className="line-container">
                    <div className="horizontal-line"></div>
                  </div>
                  <div className="summary-total">
                    <span>Final Total</span>
                    <span>RM {(finalTotal + finalTotal * SST_RATE).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={handlePlaceOrder}
                className="submit-button"
                aria-label="Place order"
              >
                Place Order
              </button>
              {useCredits && creditsToUse > 0 && (
                <p style={{ color: "#d32f2f", fontSize: "0.85rem", marginTop: "10px", textAlign: "center" }}>
                  Note: Credits are held during payment for 3 minutes and will be returned if the payment fails.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <form
        ref={paymentFormRef}
        action="https://pay.fiuu.com/RMS/pay/pickledepot/"
        method="POST"
        style={{ display: "none" }}
        acceptCharset="UTF-8"
      >
        <input type="hidden" name="merchant_id" value="" />
        <input type="hidden" name="amount" value="" />
        <input type="hidden" name="orderid" value="" />
        <input type="hidden" name="currency" value="" />
        <input type="hidden" name="vcode" value="" />
        <input type="hidden" name="bill_name" value="" />
        <input type="hidden" name="bill_email" value="" />
        <input type="hidden" name="bill_mobile" value="" />
        <input type="hidden" name="bill_desc" value="" />
        <input type="hidden" name="b_addr1" value="" />
        <input type="hidden" name="returnurl" value="" />
        <input type="hidden" name="notifyurl" value="" />
        <input type="hidden" name="callbackurl" value="" />
        <input type="hidden" name="metadata" value="" />
        <input type="hidden" name="waittime" value="" />
        <input type="hidden" name="idempotency_key" value="" />
        <input type="hidden" name="guest_checkout" value="0" />
      </form>
      {isProcessing && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="loading-spinner-container" style={{ background: 'transparent', height: 'auto' }}>
            <div className="loading-spinner"></div>
            <span style={{ marginTop: '15px', color: '#576f3f', fontFamily: '"Manrope", sans-serif', fontSize: '0.95rem' }}>Processing your booking...</span>
          </div>
        </div>
      )}
      {notification.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Notification</h2>
            <p>{notification.message}</p>
            <button className="modal-close-button" onClick={handleCloseNotification}>
              <FontAwesomeIcon icon={faTimes} /> Got It
            </button>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={handleSubmit}
      />
    </div>
  );
};

export default CoachingCheckout;
